import { generateNonce } from "@/helpers/nonce";
import { installTranslationSafeDom } from "@/helpers/translationSafeDom";
import { setupCSP } from "@/config/csp";
import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import PrimeVue from "primevue/config";
import AuthService from '@/services/auth.service';
import JWTService from "@/services/jwt.util.service";
import JwtUtilService from "@/services/jwt.util.service";
import { removeLocalValue } from "@/hooks/useLocalStorage";

import "primevue/resources/themes/lara-light-teal/theme.css";
import "bootstrap/js/dist/collapse";
import "primevue/resources/primevue.min.css";
import "primeicons/primeicons.css";
import "./styles/media-queries.scss";
import "@/styles/bootstrap.scss";

import Button from "primevue/button";
import SplitButton from "primevue/splitbutton";
import Card from "primevue/card";
import Chip from "primevue/chip";
import MultiSelect from "primevue/multiselect";
import Badge from "primevue/badge";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import InputNumber from "primevue/inputnumber";
import Checkbox from "primevue/checkbox";
import RadioButton from "primevue/radiobutton";
import Calendar from "@/components/layout/calendar";
import BaseInputNumber from "@/components/layout/BaseInputNumber.vue";
import Dropdown from "primevue/dropdown";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import ColumnGroup from "primevue/columngroup";
import Row from "primevue/row";
import Paginator from "primevue/paginator";
import Carousel from "primevue/carousel";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Tooltip from "primevue/tooltip";
import ConfirmationService from "primevue/confirmationservice";
import ConfirmDialog from "primevue/confirmdialog";
import Dialog from "primevue/dialog";
import Menu from "primevue/menu";
import Divider from "primevue/divider";
import Toast from "primevue/toast";

import VueGoogleMaps from "@fawmi/vue-google-maps";

import ToastService from "primevue/toastservice";
import { ToastSeverity } from "primevue/api";

import "@/styles/main.scss"; // global

import VueAxios from "vue-axios";
import axios from "axios";
import { STORE_ENUM } from "@/store/store.enums";

// nonce generated inline in public/index.html so it exists before any <head> script runs
const nonce = (window as any).__nonce ?? generateNonce();
(window as any).__nonce = nonce;
document.addEventListener("DOMContentLoaded", () => {
  const configScript = document.getElementById("hyperpay-config");
  if (configScript) {
    configScript.setAttribute("nonce", nonce);
  }
});
// setting up CSPin the head
setupCSP();
// Browser page translation must not crash Vue's DOM patching.
installTranslationSafeDom();

const app = createApp(App);
app.use(PrimeVue, {
  ripple: false,
  locale: store.state.localOptions,
});
import { createHead } from "@vueuse/head";
import { displayResponseErrors } from "./hooks/useDisplayResponseErrors";

const head = createHead();
app.use(head);
app.use(VueAxios, axios);
app.use(ToastService);
app.use(ConfirmationService);

app.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
  },
});
app.directive("tooltip", Tooltip);

app.component("Button", Button);
app.component("SplitButton", SplitButton);
app.component("Card", Card);
app.component("MultiSelect", MultiSelect);
app.component("Chip", Chip);
app.component("Badge", Badge);
app.component("TabView", TabView);
app.component("TabPanel", TabPanel);
app.component("InputText", InputText);
app.component("Textarea", Textarea);
app.component("InputNumber", InputNumber);
app.component("Checkbox", Checkbox);
app.component("RadioButton", RadioButton);
app.component("Calendar", Calendar);
app.component("Dropdown", Dropdown);
app.component("DataTable", DataTable);
app.component("Column", Column);
app.component("ColumnGroup", ColumnGroup);
app.component("Row", Row);
app.component("Paginator", Paginator);
app.component("Carousel", Carousel);
app.component("Accordion", Accordion);
app.component("AccordionTab", AccordionTab);
app.component("ConfirmDialog", ConfirmDialog);
app.component("Dialog", Dialog);
app.component("Menu", Menu);
app.component("Toast", Toast);
app.component("Divider", Divider);
app.component("BaseInputNumber", BaseInputNumber);

app.config.globalProperties.$t = (key: string) => key;
app.config.globalProperties.$i18n = { locale: 'ar' };

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else if (token) {
      resolve(token);
    }
  });

  failedQueue = [];
};

const refreshTokenHandler = async (): Promise<string> => {
  if (process.env.VUE_APP_AUTHENTICATION_MODE === "custom") {
    const refreshToken = JwtUtilService.getToken(JwtUtilService.refresh_token_key);
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const { data } = await AuthService.getCustomRefreshToken(refreshToken);
      const newAccessToken = data.result.access;

      JwtUtilService.setToken(JwtUtilService.access_token_key, newAccessToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;

      return newAccessToken;
    } catch (error) {
      console.error("Custom refresh token failed:", error);
      throw error;
    }
  } else {
    const realm = JwtUtilService.getClientID()
      ?.split("-")
      .slice(0, 2)
      .join("-");
    const params = new URLSearchParams();
    params.append("grant_type", "refresh_token");
    params.append(
      "refresh_token",
      JwtUtilService.getToken(JwtUtilService.refresh_token_key) || ""
    );
    params.append("client_id", JwtUtilService.getClientID() || "");

    const url =
      process.env.VUE_APP_KEYCLOAK_URL +
      `/realms/${realm}/protocol/openid-connect/token`;

    try {
      const { data } = await axios.post(url, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      JwtUtilService.setToken(JwtUtilService.access_token_key, data.access_token);
      JwtUtilService.setToken(JwtUtilService.refresh_token_key, data.refresh_token);
      JwtUtilService.setToken(JwtUtilService.id_token_key, data.id_token);

      axios.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;

      return data.access_token;
    } catch (error) {
      console.error("Keycloak refresh token failed:", error);
      throw error;
    }
  }
};

axios.interceptors.request.use(
  (config) => {
    setAuthHeader(config);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.status === 500) {
      // Check if response has errors array then iterate over it and display each error message in a toast
      // Else display generic error message
      if (error?.response && error?.response?.data?.errors) {
        displayResponseErrors(
          error.response.data.errors,
          app.config.globalProperties.$toast,
          { processMessage: true, duration: 3000 }
        );
      } else {
        app.config.globalProperties.$toast.add({
          severity: "error",
          detail: "حدث خطأ في النظام",
          life: 3000,
        });
      }
    } else if (error?.response?.status === 403) {
      if (originalRequest.skipErrorHandling) {
        return Promise.reject(error);
      }

      let code = error?.response?.data?.result?.code;
      let url = "";
      if (code === "RESET_PASSWORD") {
        url = "/reset-password";
      } else if (code === "UPLOAD_AGREEMENT") {
        url = "/upload-signed-agreement";
      } else if (code === "SIGN_AGREEMENT") {
        url = "/agreement-approval";
      }

      window.location.href = process.env.VUE_APP_FE_URL + url;
    } else if (error?.response?.status === 401) {
      if (localStorage.getItem("isMobilePaymentFlow") === "true") {
        localStorage.removeItem("isMobilePaymentFlow");
        return Promise.reject(error);
      }
      if (originalRequest.url.includes("/account/auth/refresh/")) {
        store.dispatch(STORE_ENUM.SIGN_OUT);
        delete axios.defaults.headers.common["Authorization"];
        router.push("/sign-in");
        return Promise.reject(error);
      }

      if (!originalRequest._retry) {
        if (localStorage.getItem("isMobilePaymentFlow") === "true") {
          localStorage.removeItem("isMobilePaymentFlow");
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axios(originalRequest);
          }).catch((err) => {
            return Promise.reject(err);
          });
        }

        isRefreshing = true;

        try {
          const newToken = await refreshTokenHandler();

          processQueue(null, newToken);

          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(originalRequest);

        } catch (refreshError) {
          processQueue(refreshError, null);

          store.dispatch(STORE_ENUM.SIGN_OUT);

          delete axios.defaults.headers.common["Authorization"];

          router.push("/sign-in");
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    } else if (
      error?.response?.status >= 400 &&
      error?.response?.status !== 406
    ) {
      if (originalRequest.skipErrorHandling ||  originalRequest.url.includes("/account/auth/refresh/")) {
        return Promise.reject(error);
      }
      displayResponseErrors(
        error.response?.data?.errors,
        app.config.globalProperties.$toast
      );
    }
    return Promise.reject(error);
  }
);

let setAuthHeader = (config: any) => {
  let token = JWTService.getToken(JwtUtilService.access_token_key);

  if (
    token &&
    config.url.includes(process.env.VUE_APP_API_URL) &&
    !config.headers["Authorization"]
  ) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
};
// globally providing static URL
const staticUrl = process.env.VUE_APP_STATIC_URL;
app.provide("staticUrl", staticUrl);

const initializeAuth = async () => {
  if (process.env.VUE_APP_AUTHENTICATION_MODE === "custom") {
    const accessToken = JwtUtilService.getToken(JwtUtilService.access_token_key);
    const refreshToken = JwtUtilService.getToken(JwtUtilService.refresh_token_key);
    
    if (!accessToken && refreshToken) {
      try {
        const newAccessToken = await refreshTokenHandler();
        store.commit(STORE_ENUM.SET_AUTHENTICATED, true);
      } catch (error) {
        console.error("Auto-refresh failed, user needs to login again");
      }
    }
  }

  if (!JwtUtilService.getToken(JwtUtilService.access_token_key)) {
    removeLocalValue("donor_mobile_number");
  }
};

// Initializing auth and mount app
initializeAuth().finally(() => {
  app.use(store).use(router).mount("#app");
});